import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { addressesIncludeKey, phoneNumberIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import {
  agreementQuerySchema,
  type AgreementQuerySchema,
  type AgreementsAllQuerySchema,
  type AgreementsCreateSchema,
  type AgreementUpdateSchema,
} from './schemas';

const includeKeysBase = [
  addressesIncludeKey(),
  addressesIncludeKey('addendums'),
  'client',
  'addendums.client',
  'addendums.account_manager.profile_image',
  'addendums.primary_domains',
  'addendums.agreement_deliverables.product.category',
  'addendums.agreement_deliverables.price_frequency',
  'account_manager.profile_image',
  'primary_domains',
  'agreement_deliverables.product.category',
  'agreement_deliverables.price_frequency',
];

const includeKeys = [
  ...includeKeysBase,
  'parent',
  addressesIncludeKey(),
  'agreement_deliverables.deliverable_wholesale_price',
  'agreement_signer',
  'brand.icon',
  'partner.icon',
  'terms_condition_template',
  'billing_term',
  'agreement_contact.profile_image',
  'billing_contact.profile_image',
  'project_contact.profile_image',
  'emergency_contact.profile_image',
  phoneNumberIncludeKey('emergency_contact'),

  'billing_email_contacts.emails',
  'billing_email_contacts.profile_image',
  phoneNumberIncludeKey('billing_email_contacts'),

  'client.partner.icon',
  'client.brand.icon',
  addressesIncludeKey('client'),
  'client.logo',
  'client.contacts.emails',
  'client.contacts.profile_image',
  phoneNumberIncludeKey('client.contacts'),
  'client.domains',
  'agreement_deliverables.product.unit_sell_by',
  'agreement_deliverables.product.price_frequency',
  'agreement_installments.agreement_payments',
  'agreement_installments.invoice',
  'agreement_draft',
  'fully_executed_agreement',

  phoneNumberIncludeKey('account_manager'),
  'agreement_commissions.commission_plan.commission_tiers',
  'agreement_commissions.team_member.profile_image',

  'subsidies.client.logo',
  addressesIncludeKey('subsidies'),
  addressesIncludeKey('subsidies.client'),
  'subsidies.client.contacts.profile_image',
  'subsidies.primary_contact.profile_image',
  phoneNumberIncludeKey('subsidies.primary_contact'),
  'subsidies.subsidy_deliverables.agreement_deliverable',
];

const singleAgreementIncKeys = includeKeys.join(',');

export const useCreateMutation = () =>
  useMutation<{ data: AgreementQuerySchema }, ApiError, AgreementsCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/agreements/',
        payload,
      }),
  });

// for performance reasons, we are keeping default records to 100
export function getAgreementsQuery(apiParamStr = `?per_page=100&page=1&filter[advance_filtering]=doesnt_have[parent]`) {
  return {
    queryKey: ['AGREEMENTS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<AgreementsAllQuerySchema>({
        endpoint: '/api/v1/agreements',
        params: `${apiParamStr}&include=${includeKeysBase.join(',')}`,
      });
      return res;
    },
  };
}

export function useGetAgreements(apiParamStr?: string) {
  return useSuspenseQuery(getAgreementsQuery(apiParamStr));
}

export function useGetAgreement(id: string) {
  return useSuspenseQuery(getAgreementQuery(id));
}

export function getAgreementQuery(id: string) {
  return {
    queryKey: ['AGREEMENT', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: AgreementQuerySchema }>({
        endpoint: `/api/v1/agreements/${id}`,
        params: `include=${singleAgreementIncKeys}`,
        responseSchema: agreementQuerySchema,
        preventAuditLog: true,
      });

      return {
        ...res,
        data: {
          ...res.data,
          client: {
            ...res.data.client,
            contacts: orderBy(res.data.client?.contacts ?? [], ['first_name']),
          },
        },
      };
    },
  };
}

export const useGetAgreementsMutation: UseControlledMutation<AgreementQuerySchema> = () =>
  useMutation({
    mutationFn: async (apiParams) => {
      return queryClient.fetchQuery({
        queryKey: ['AGREEMENTS_ASYNC', apiParams ?? {}],
        staleTime: Infinity,
        queryFn: async () => {
          let include = 'logo';
          if (apiParams?.include) {
            include = apiParams.include;
          }
          const res = await apiClient.get<AgreementsAllQuerySchema>({
            endpoint: '/api/v1/agreements',
            params: { ...apiParams, include },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export const invalidateAgreementQuery = async (id: string) => {
  await queryClient.invalidateQueries({ queryKey: ['AGREEMENT', id] });
};

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: AgreementQuerySchema }, ApiError, AgreementUpdateSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/agreements/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'agreements', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: AgreementQuerySchema }, ApiError, Partial<AgreementUpdateSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/agreements/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['AGREEMENT_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/agreements/${id}`,
        method: 'DELETE',
      }),
  });

export const useCreateAgreementIdMutation = () =>
  useMutation<{ data: string }, ApiError, { agreement_date?: string; client_id?: number; parent_id?: number }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/agreements/generate-id',
        payload,
      }),
  });

export const useGetAgreementPdfMutation = (id: string) =>
  useMutation<
    {
      data: {
        name: string;
        pdf_url: string;
      };
    },
    ApiError
  >({
    mutationFn: () =>
      apiClient.request({
        endpoint: `/api/v1/agreements/generate-pdf/${id}`,
        params: `include=agreement_contact.profile_image,billing_contact.profile_image,project_contact.profile_image,emergency_contact.profile_image,emergency_contact.phone_numbers.country.flag,client.partner.icon,client.brand.icon,client.brand.agreement_logo,client.addresses.state,client.addresses.country,client.logo,client.contacts.profile_image,client.contacts.phone_numbers.country.flag,client.domains,account_manager.profile_image,primary_domains,agreement_deliverables.product.category,agreement_deliverables.product.price_frequency,agreement_deliverables.price_frequency`,
        method: 'GET',
        init: { headers: { 'Content-Type': 'application/pdf', 'response-type': 'blob' } },
      }),
  });

type VerifyLockdownOverridePinPayload = {
  lockdown_override_pin: string;
};

export const useVerifyLockdownOverridePinMutation = () =>
  useMutation<boolean, ApiError, VerifyLockdownOverridePinPayload>({
    mutationFn: async (payload) => {
      const res = await apiClient.request<{ valid: boolean }, VerifyLockdownOverridePinPayload>({
        endpoint: '/api/v1/verify-lockdown-pin',
        payload,
      });

      return Boolean(res?.valid);
    },
  });
